/**
 * L2 drill-down: every Service Bus queue the integration workspace reported,
 * with a URL-addressable detail drawer per queue.
 *
 * **Today this page has no data source at all, and it says so.** It reads the
 * `AzureMetrics` table of a *second* Log Analytics workspace, and nothing about
 * that path is configured: `.env.example` sets none of the `NOVA_FO_SB_*` or
 * `NOVA_FO_SERVICE_BUS_*` variables, so the backend falls back to the resource
 * ID and namespace the Power BI model named — and that resource ID answers 404
 * (`nova.fo.workspaces`, measured 2026-08-20). Nothing has ever reached this
 * screen and nothing will until somebody configures it.
 *
 * So the page carries a **setup panel**, in the shape `features/cloud/
 * not-configured.tsx` set: name the problem, list the concrete fixes with the
 * real setting names, close by saying nothing here is simulated. Two things had
 * to be true for that to be worth writing rather than leaving the generic access
 * card alone — the access card explains the *read* that failed, and it cannot
 * know that a corrected workspace address on its own still yields nothing
 * without a diagnostic setting exporting the namespace's metrics into it.
 *
 * The rule underneath both panels: an empty list here would read as "there are
 * no queues", which is a different — and much more reassuring — claim than
 * "nothing was ever measured".
 */

import { Cable, Icon } from "@norican/icons";
import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  Card,
  DataGrid,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { DetailDrawer } from "@/components/board";
import { type FoQueueHealth, useFoIntegration } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import { PanelCard } from "./panel-card";
import { DYNAMICS_PATH, DYNAMICS_QUEUES_PATH, QUEUE_DRAWER_PARAM } from "./params";
import { formatCount, formatMessageCount, formatWindow, HEALTH_LABEL, HEALTH_TONE } from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Dynamics",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to={DYNAMICS_PATH} className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Queues", current: true },
];

interface QueuesSetupPanelProps {
  /** Why there is nothing on screen, in the caller's own words. */
  lead: string;
  /** The Service Bus namespace NOVA is currently configured to match on. */
  namespace: string;
  /**
   * The workspace resource ID that was queried. Passed only when no
   * `FoAccessPanel` is on the page — that card already echoes it, and one
   * resource ID printed twice reads as two different addresses.
   */
  resourceId?: string;
}

/**
 * What this screen shows when no Service Bus metrics can reach it.
 *
 * Same grammar as `CloudSetupPanel`: an outlined warning card, the state named
 * in the heading, concrete fixes carrying the exact setting names an operator
 * has to paste somewhere, and a closing line that this board shows nothing
 * rather than something plausible. The three fixes are independent and all
 * three are required — the common failure is correcting the workspace address
 * and expecting queues to appear, when the namespace is not exporting metrics
 * into any workspace at all.
 */
function QueuesSetupPanel({ lead, namespace, resourceId }: QueuesSetupPanelProps) {
  return (
    <Card variant="outline" accent="left" accentTone="warning">
      <Card.Header>
        <div className="flex items-center gap-3">
          <Icon icon={Cable} size="md" />
          <Typography variant="heading-4" as="h2">
            Service Bus queue metrics are not configured
          </Typography>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          <Alert tone="warning" title="No queue metric has reached NOVA">
            {lead}
          </Alert>

          <div className="flex flex-col gap-2">
            <Typography variant="body-sm" tone="muted">
              This screen reads Azure Monitor&apos;s platform metrics for one Service Bus namespace
              out of the <code className="text-fg">AzureMetrics</code> table in a second Log
              Analytics workspace — a different workspace from every other panel on the Dynamics
              board. Three things have to be true before a queue can appear, none of them is set in{" "}
              <code className="text-fg">.env.example</code>, and all three are needed:
            </Typography>
            <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
              <li>
                <span className="text-fg font-medium">A workspace that exists</span> — set{" "}
                <code className="text-fg">NOVA_FO_SB_WORKSPACE_RESOURCE_ID</code>, or the triple{" "}
                <code className="text-fg">NOVA_FO_SB_WORKSPACE_NAME</code>,{" "}
                <code className="text-fg">NOVA_FO_SB_WORKSPACE_RESOURCE_GROUP</code> and{" "}
                <code className="text-fg">NOVA_FO_SB_WORKSPACE_SUBSCRIPTION_ID</code>. The current
                value is the one the Power BI report named, which was never validated. NOVA&apos;s
                identity then needs <strong className="text-fg">Log Analytics Reader</strong> at
                that workspace scope.
              </li>
              <li>
                <span className="text-fg font-medium">Metrics flowing into it</span> — a diagnostic
                setting on the Service Bus namespace with the{" "}
                <strong className="text-fg">AllMetrics</strong> category, pointed at that workspace.
                Without one the <code className="text-fg">AzureMetrics</code> table holds nothing
                about the namespace, however correct the address is.
              </li>
              <li>
                <span className="text-fg font-medium">The namespace named</span> —{" "}
                <code className="text-fg">NOVA_FO_SERVICE_BUS_NAMESPACE</code>, currently{" "}
                <code className="text-fg">{namespace}</code>. Leave{" "}
                <code className="text-fg">NOVA_FO_SERVICE_BUS_QUEUES</code> empty, as it is by
                default: the screen then lists every queue the namespace reports, so a newly
                provisioned queue appears without a deploy.
              </li>
            </ul>
          </div>

          {resourceId && (
            <Typography variant="body-sm" tone="muted">
              Workspace queried: <code className="text-fg text-xs break-all">{resourceId}</code>
            </Typography>
          )}

          <Typography variant="body-sm" tone="muted">
            Until then this screen stays empty. Nothing on this board is simulated, sampled or
            carried over from a previous read, so an unconfigured namespace shows nothing rather
            than a drained queue.
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}

export function DynamicsQueuesPage() {
  const integration = useFoIntegration();
  const drawer = useDrawerParam(QUEUE_DRAWER_PARAM);
  const data = integration.data;

  const openQueue = drawer.value
    ? data?.queues.find((row) => row.queue === drawer.value)
    : undefined;

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Service Bus queues"
        subtitle="Backlog, throughput and dead-lettered messages per queue, from Azure Monitor's platform metrics for the integration namespace — a different workspace from the rest of the Dynamics board."
        actions={
          data?.collected_at ? (
            <Freshness
              label="Collected"
              since={data.collected_at}
              staleAfter={data.cache_ttl_sec * 2000}
            />
          ) : undefined
        }
      />

      {integration.error && (
        <Alert tone="danger" title="Couldn't load queue health">
          {integration.error instanceof Error
            ? integration.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not an Azure one."}
        </Alert>
      )}

      {integration.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!isPanelOk(data) ? (
          <>
            <FoAccessPanel
              panel="Service Bus queue health"
              state={data}
              resourceId={data.workspace_resource_id}
              namespace={data.namespace}
            />
            {/* The card above says what the read did. This says what has to be
                in place before there is anything to read — including the half
                the access state cannot see, since a corrected workspace address
                still yields nothing while the namespace exports no metrics. */}
            <QueuesSetupPanel
              lead="The read above failed, so no queue metric reached this screen. Correcting it is one of three things that have to be true before a queue can appear here."
              namespace={data.namespace}
            />
          </>
        ) : data.queues.length === 0 ? (
          <>
            {data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}
            <QueuesSetupPanel
              lead={`The ${data.workspace} workspace answered and held no Service Bus metric for ${data.namespace} in the ${formatWindow(data.window)}. That is an absence of measurement, not a set of empty queues.`}
              namespace={data.namespace}
              resourceId={data.workspace_resource_id}
            />
          </>
        ) : (
          <>
            {data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}

            <Typography variant="body-sm" tone="muted">
              Namespace {data.namespace}, {formatWindow(data.window)}, read from Azure Monitor
              platform metrics in the {data.workspace} workspace. Backlog figures are 5-minute
              means, so they describe a shape over the window rather than a message count at any
              instant.{" "}
              {data.queues_requested.length > 0
                ? `Scoped to ${data.queues_requested.length} configured queues, so a newly provisioned queue is missing from this list until the scope is widened.`
                : "No queue scope is configured, so this is every queue the namespace produced metrics for — a new queue appears here without a deploy."}
            </Typography>

            <PanelCard
              title="Queues"
              caption={`${formatCount(data.queues.length)} reporting · degraded above ${formatCount(
                data.warning_backlog,
              )}, failing above ${formatCount(data.critical_backlog)} active messages`}
              padding="none"
            >
              <DataGrid<FoQueueHealth>
                data={data.queues}
                getRowId={(row) => row.queue}
                density="compact"
                aria-label="Every Service Bus queue and its backlog"
                columns={[
                  {
                    id: "queue",
                    header: "Queue",
                    accessor: (row) => row.queue,
                    sortable: true,
                    cell: (row) => (
                      <button
                        type="button"
                        className="text-fg text-left font-medium hover:underline"
                        onClick={() => drawer.open(row.queue)}
                      >
                        {row.queue}
                      </button>
                    ),
                  },
                  {
                    id: "state",
                    header: "Verdict",
                    accessor: (row) => row.state,
                    sortable: true,
                    cell: (row) => (
                      <Badge tone={HEALTH_TONE[row.state]} variant="subtle" size="xs">
                        {HEALTH_LABEL[row.state]}
                      </Badge>
                    ),
                  },
                  {
                    id: "peak",
                    header: "Peak backlog",
                    align: "end",
                    accessor: (row) => row.peak_active_messages,
                    sortable: true,
                    cell: (row) => (
                      <span className="tabular-nums">
                        {formatMessageCount(row.peak_active_messages)}
                      </span>
                    ),
                  },
                  {
                    id: "active",
                    header: "Mean backlog",
                    align: "end",
                    accessor: (row) => row.active_messages,
                    sortable: true,
                    cell: (row) => (
                      <span className="tabular-nums">
                        {formatMessageCount(row.active_messages)}
                      </span>
                    ),
                  },
                  {
                    id: "in",
                    header: "Enqueued",
                    align: "end",
                    accessor: (row) => row.incoming_messages,
                    sortable: true,
                    cell: (row) => (
                      <span className="tabular-nums">{formatCount(row.incoming_messages)}</span>
                    ),
                  },
                  {
                    id: "out",
                    header: "Dequeued",
                    align: "end",
                    accessor: (row) => row.outgoing_messages,
                    sortable: true,
                    cell: (row) => (
                      <span className="tabular-nums">{formatCount(row.outgoing_messages)}</span>
                    ),
                  },
                  {
                    id: "dead",
                    header: "Dead-lettered",
                    align: "end",
                    accessor: (row) => row.dead_lettered_messages,
                    sortable: true,
                    cell: (row) => (
                      <span className="tabular-nums">
                        {formatMessageCount(row.dead_lettered_messages)}
                      </span>
                    ),
                  },
                ]}
              />
            </PanelCard>

            {openQueue && (
              <DetailDrawer
                open
                onClose={drawer.close}
                title={openQueue.queue}
                breadcrumbs={[
                  { label: "Dynamics", to: DYNAMICS_PATH },
                  { label: "Queues", to: DYNAMICS_QUEUES_PATH },
                ]}
                items={[
                  { label: "Verdict", value: HEALTH_LABEL[openQueue.state] },
                  { label: "Namespace", value: data.namespace },
                  {
                    label: "Peak active messages",
                    value: formatMessageCount(openQueue.peak_active_messages),
                  },
                  {
                    label: "Mean active messages",
                    value: formatMessageCount(openQueue.active_messages),
                  },
                  { label: "Enqueued", value: formatCount(openQueue.incoming_messages) },
                  { label: "Dequeued", value: formatCount(openQueue.outgoing_messages) },
                  {
                    label: "Dead-lettered (peak)",
                    value: formatMessageCount(openQueue.dead_lettered_messages),
                  },
                  { label: "Degraded above", value: formatCount(data.warning_backlog) },
                  { label: "Failing above", value: formatCount(data.critical_backlog) },
                  { label: "Window", value: formatWindow(data.window) },
                  { label: "Workspace", value: data.workspace },
                ]}
              />
            )}
          </>
        ))
      )}
    </main>
  );
}
